import { addDays, differenceInDays, format } from "date-fns"
import PropTypes from "prop-types"
import React, { useState } from "react"
import { useMutation, useQueryClient } from "react-query"
import {
  AddToWaitlistAction,
  CreateContractAction,
  CreateReservationAction,
  DeclineAction,
  InProgressAction,
  MarkAsCompleteAction,
  ViewContractAction,
  ViewCustomerAction,
  ViewLeadAction,
  ViewReservationAction,
  ViewWaitlistAction,
} from "src/actions/inquiries"
import { useMarina } from "src/contexts/MarinaContext"
import { DockwaLogo } from "src/main/Reservations/DockwaLogo"

import Badge from "src/components/Badge"
import { statusToken } from "src/components/Badge/status_tokens"
import BoatPreviewLink from "src/components/BoatPreviewLink"
import ClickablePhoneNumber from "src/components/ClickablePhoneNumber"
import CollapsibleBar from "src/components/CollapsibleBar"
import DataTableV2 from "src/components/DataTableV2"
import InsightCard from "src/components/InsightCard"
import InsightSummary from "src/components/InsightSummary"
import Tooltip from "src/components/Tooltip"

import {
  getInquiries,
  getInquiriesSelectableIds,
  printInquiries,
  updateInquiryStatus,
} from "src/api/Inquiries"

import useDatatable from "src/hooks/use_datatable"
import { useToast } from "src/hooks/use_toast"

import { inchesToFeet } from "src/utils/UnitConversion"
import { snakecaseToTitlecase } from "src/utils/string_helpers"

import AcceptToContractGroupModal from "./AcceptToContractGroupModal"
import DeclineModal from "./DeclineModal"
import FilterChips from "./FilterChips"
import Filters from "./Filters"
import ModuleTiles from "./ModuleTiles"
import SubmissionFormModal from "./SubmissionFormModal"
import { ContactName, ContractGroup, SpecialRequest } from "./row_components"

// Kept in sync with app/views/manage/partials/_insight_data_card.slim's
// kit_icon_classes — icon classes with no Classic equivalent that stay on
// the custom kit font while the rest of the insight icons render as
// fa-solid.
const KIT_INSIGHT_ICON_CLASSES = [
  "fa-dry-storage",
  "fa-maintenance",
  "fa-boat-dealership",
]

const renderSource = (row) => {
  const boaterSource = row.source === "boater"
  return (
    <div className="w-min">
      <Tooltip text={row.metaSource} placement="left" variant="dark">
        {boaterSource ? (
          <DockwaLogo />
        ) : (
          <i className="fa-solid fa-computer-mouse text-2xl text-gray-500" />
        )}
      </Tooltip>
    </div>
  )
}

const renderSubmittedDate = ({ createdAt, status }) => {
  const date = new Date(createdAt)
  const today = new Date()
  const daysUntilExpiration = 8 - differenceInDays(today, date)

  if (daysUntilExpiration > 0 && status === "pending") {
    return (
      <div className="w-min">
        <Tooltip
          text={`Expires in ${daysUntilExpiration} days`}
          placement="left"
          variant="dark"
        >
          <span className="flex items-center text-yellow-700">
            {createdAt}
            <i className="fa-solid fa-calendar-clock -mt-0.5 ml-1" />
          </span>
        </Tooltip>
      </div>
    )
  } else if (status === "expired") {
    return (
      <div className="w-min">
        <Tooltip
          text={`Expired on ${format(addDays(date, 8), "MM/dd/yy")}`}
          placement="left"
          variant="dark"
        >
          <span className="flex items-center text-red-700">
            {createdAt}
            <i className="fa-solid fa-calendar-clock -mt-0.5 ml-1" />
          </span>
        </Tooltip>
      </div>
    )
  } else {
    return <span>{createdAt}</span>
  }
}

const BASE_COLUMNS = [
  {
    key: "status",
    header: "Status",
    cardRole: "hidden",
    width: "110px",
    render: (row) =>
      row.status ? (
        <Badge
          color={statusToken(snakecaseToTitlecase(row.status))}
          text={snakecaseToTitlecase(row.status)}
          variant="small"
        />
      ) : null,
  },
  {
    key: "contactName",
    header: "Captain",
    sortable: true,
    cardRole: "title",
    cardOrder: 0,
    render: (row) => <ContactName contactName={row.contact?.name} />,
    cardRender: (row) => (
      <div className="space-y-1">
        <div className="flex w-full items-center justify-between gap-1">
          <div className="min-w-0 truncate">{row.contact?.name}</div>
          {row.status && (
            <Badge
              color={statusToken(snakecaseToTitlecase(row.status))}
              text={snakecaseToTitlecase(row.status)}
              variant="small"
            />
          )}
        </div>
        {row.contactBoat && (
          <div className="flex items-center gap-2 text-sm font-normal">
            <BoatPreviewLink boat={row.contactBoat} />
            {row.contactBoat.lengthOverall && (
              <span className="text-xs text-gray-600">
                LOA: {inchesToFeet(row.contactBoat.lengthOverall)}&apos;
              </span>
            )}
          </div>
        )}
        <div className="flex items-center gap-2 text-sm font-normal text-gray-700">
          {(row.startDate || row.endDate) && (
            <span>
              {row.startDate} &ndash; {row.endDate}
            </span>
          )}
          {row.transactionType && (
            <>
              <span className="text-gray-400">&middot;</span>
              <span className="text-gray-500">{row.transactionType}</span>
            </>
          )}
        </div>
      </div>
    ),
  },
  {
    key: "boatName",
    header: "Boat",
    sortable: true,
    cardRole: "hidden",
    render: (row) =>
      row.contactBoat ? <BoatPreviewLink boat={row.contactBoat} /> : null,
  },
  {
    key: "phone",
    header: "Phone",
    hide: true,
    render: (row) => (
      <ClickablePhoneNumber
        phone={row.contact?.phone}
        displayPhone={row.contact?.displayPhone}
      />
    ),
  },
  {
    key: "loa",
    header: "LOA",
    sortable: true,
    width: "75px",
    cardRole: "hidden",
    render: (row) =>
      row.contactBoat?.lengthOverall
        ? `${inchesToFeet(row.contactBoat.lengthOverall)}'`
        : "",
  },
  {
    key: "beam",
    header: "Beam",
    width: "75px",
    sortable: true,
    hide: true,
    cardRole: "hidden",
    render: (row) =>
      row.contactBoat?.beam ? `${inchesToFeet(row.contactBoat.beam)}'` : "",
  },
  {
    key: "draw",
    header: "Draft",
    width: "75px",
    sortable: true,
    hide: true,
    cardRole: "hidden",
    render: (row) =>
      row.contactBoat?.draw ? `${inchesToFeet(row.contactBoat.draw)}'` : "",
  },
  {
    key: "height",
    header: "Height",
    width: "75px",
    sortable: true,
    hide: true,
    cardRole: "hidden",
    render: (row) =>
      row.contactBoat?.height ? `${inchesToFeet(row.contactBoat.height)}'` : "",
  },
  {
    key: "createdAt",
    header: "Submitted",
    sortable: true,
    render: (row) => renderSubmittedDate(row),
  },
  {
    key: "source",
    header: "Source",
    width: "75px",
    cardRole: "hidden",
    render: renderSource,
  },
  {
    key: "transactionType",
    header: "Category",
    cardRole: "hidden",
  },
  {
    key: "startDate",
    header: "Start",
    width: "110px",
    sortable: true,
    cardRole: "hidden",
  },
  {
    key: "endDate",
    header: "End",
    width: "110px",
    sortable: true,
    cardRole: "hidden",
  },
  {
    key: "specialRequest",
    header: "Requests",
    render: (row) => <SpecialRequest specialRequest={row.specialRequest} />,
    width: "300px",
  },
]

const CONTRACT_GROUP_COLUMN = {
  key: "contractGroup",
  header: "Contract Group",
  sortable: true,
  render: (row) =>
    row.contractGroup ? (
      <ContractGroup contractGroup={row.contractGroup} />
    ) : null,
}

const ManageInquiries = ({
  archivedContractGroupsPath,
  contractsEnabled,
  generalInquiriesPath,
  groups,
  insights,
  inquiryStatuses,
  inquiryTransactionTypes,
  isClaim,
  longestLoa,
  longtermWaitlistEnabled,
}) => {
  const { marinaSlug } = useMarina()
  const showToast = useToast()
  const queryClient = useQueryClient()

  const [isSubmissionFormModalOpen, setIsSubmissionFormModalOpen] =
    useState(false)
  const [showAcceptModal, setShowAcceptModal] = useState(false)
  const [showDeclineModal, setShowDeclineModal] = useState(false)
  const [selectedInquiry, setSelectedInquiry] = useState(null)
  const [selectedContactId, setSelectedContactId] = useState(null)

  const table = useDatatable({
    queryKey: "inquiries",
    fetchFn: (params) => getInquiries(marinaSlug, params),
    defaultFilters: {
      search: "",
      statuses: [],
      categories: [],
      loaMin: null,
      loaMax: null,
      contractGroups: [],
    },
    fetchAllIds: (filterParams) =>
      getInquiriesSelectableIds(marinaSlug, filterParams).then((r) => r.ids),
    idKey: "encodedId",
    staleTime: 30000,
  })

  const { mutate: updateStatus, isLoading: updatingStatus } = useMutation(
    (data) => updateInquiryStatus(marinaSlug, { data }),
    {
      onSuccess: (data) => {
        queryClient.invalidateQueries("inquiries")
        showToast(data.message, { type: "success" })
        setShowDeclineModal(false)
        if (data.status === "waitlisted") {
          window.location.assign(
            `/manage/${marinaSlug}/waitlists/long_term/entries`
          )
        }
      },
      onError: (error) => {
        showToast(error.message, { type: "error" })
      },
    }
  )

  const handleDecline = (encodedId) => {
    setShowDeclineModal(true)
    setSelectedContactId(encodedId)
  }

  const handleCreateContract = (row) => {
    setShowAcceptModal(true)
    setSelectedInquiry(row)
  }

  const activeColumns = contractsEnabled
    ? [
        ...BASE_COLUMNS.slice(0, BASE_COLUMNS.length - 1),
        CONTRACT_GROUP_COLUMN,
        BASE_COLUMNS[BASE_COLUMNS.length - 1],
      ]
    : BASE_COLUMNS

  const ACTIONS = [
    ViewLeadAction({ marinaSlug }),
    ViewCustomerAction({ marinaSlug }),
    InProgressAction({ onUpdate: updateStatus }),
    ...(contractsEnabled && groups.length > 0
      ? [CreateContractAction({ onCreateContract: handleCreateContract })]
      : []),
    ...(!isClaim ? [CreateReservationAction({ marinaSlug })] : []),
    ...(longtermWaitlistEnabled
      ? [AddToWaitlistAction({ onUpdate: updateStatus })]
      : []),
    MarkAsCompleteAction({ onUpdate: updateStatus }),
    DeclineAction({ onDecline: handleDecline }),
    ViewWaitlistAction({ marinaSlug }),
    ViewContractAction({ marinaSlug }),
    ViewReservationAction({ marinaSlug }),
  ]

  return (
    <div className="bg-white px-2 lg:px-6">
      {isClaim && (
        <div className="print:hidden">
          <ModuleTiles />
        </div>
      )}

      {insights && (
        <div className="mb-4 print:hidden">
          <CollapsibleBar
            title={
              <span className="text-blue-800">
                Insights <i className="fa-solid fa-stars" />{" "}
              </span>
            }
            ariaLabel="Toggle Insights panel"
            accent="bg-gradient-to-r from-blue-500 via-blue-900 to-blue-700"
            summary={
              <InsightSummary
                items={insights.insightsToDisplay.map((i) => ({
                  value: i.dataPoint,
                  label: i.title,
                }))}
              />
            }
          >
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
              {insights.insightsToDisplay.map((insight) => (
                <InsightCard
                  key={insight.title}
                  icon={insight.iconClass}
                  iconStyle={
                    KIT_INSIGHT_ICON_CLASSES.includes(insight.iconClass)
                      ? "kit"
                      : "solid"
                  }
                  title={insight.title}
                  value={insight.dataPoint}
                  description={insight.description}
                />
              ))}
            </div>
          </CollapsibleBar>
        </div>
      )}

      <div className="print:hidden">
        <Filters
          table={table}
          inquiryStatuses={inquiryStatuses}
          inquiryTransactionTypes={inquiryTransactionTypes}
          longestLoa={longestLoa}
          contractsEnabled={contractsEnabled}
          groups={groups}
          archivedContractGroupsPath={archivedContractGroupsPath}
          onOpenSubmissionFormModal={() => setIsSubmissionFormModalOpen(true)}
        />
        <FilterChips table={table} groups={groups} />
      </div>

      <div className="z-0 mt-4 overflow-auto pb-12">
        <DataTableV2
          name="inquiries"
          columns={activeColumns}
          data={table.records}
          actions={ACTIONS}
          maxHeight="none"
          emptyState={
            <div className="flex flex-col text-center">
              <span className="mb-2 text-xl font-bold">
                No leads to display
              </span>
              <span className="text-gray-600">
                Try adjusting your filters to find what you&apos;re looking for.
              </span>
            </div>
          }
          rowCount={15}
          onPrint={(ids) => {
            const { page, ...printParams } = table.fetchParams
            return printInquiries({ marinaSlug, ids, ...printParams })
          }}
          {...table.tableProps}
        />
      </div>

      <SubmissionFormModal
        setIsOpen={setIsSubmissionFormModalOpen}
        isOpen={isSubmissionFormModalOpen}
        generalInquiriesPath={generalInquiriesPath}
        groups={groups}
        contractsEnabled={contractsEnabled}
      />
      <AcceptToContractGroupModal
        marinaSlug={marinaSlug}
        contractGroups={groups}
        isOpen={showAcceptModal}
        selectedInquiry={selectedInquiry}
        contractGroup={selectedInquiry?.contractGroupId}
        setIsOpen={setShowAcceptModal}
      />
      <DeclineModal
        isOpen={showDeclineModal}
        setIsOpen={setShowDeclineModal}
        selectedContact={selectedContactId}
        updateStatus={(data) => updateStatus(data)}
        isLoading={updatingStatus}
      />
    </div>
  )
}

ManageInquiries.propTypes = {
  archivedContractGroupsPath: PropTypes.string,
  contractsEnabled: PropTypes.bool.isRequired,
  generalInquiriesPath: PropTypes.string.isRequired,
  groups: PropTypes.arrayOf(
    PropTypes.shape({
      encodedId: PropTypes.string,
      name: PropTypes.string,
    })
  ).isRequired,
  insights: PropTypes.shape({
    insightsToDisplay: PropTypes.arrayOf(
      PropTypes.shape({
        title: PropTypes.string.isRequired,
        description: PropTypes.string.isRequired,
        iconClass: PropTypes.string.isRequired,
        dataPoint: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
          .isRequired,
      })
    ).isRequired,
  }),
  inquiryStatuses: PropTypes.array.isRequired,
  inquiryTransactionTypes: PropTypes.array.isRequired,
  isClaim: PropTypes.bool.isRequired,
  longestLoa: PropTypes.number,
  longtermWaitlistEnabled: PropTypes.bool.isRequired,
}

export default ManageInquiries
